import React from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Users,
  PhoneCall,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  PhoneIncoming
} from 'lucide-react';

export default function MessagesTab({
  commStats,
  conversations,
  calls,
  expandedConversation,
  setExpandedConversation,
  formatDate
}) {
  return (
    <div className="space-y-6">
      {/* Communication Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <Phone className="w-5 h-5 text-emerald-400" />
            <span className="text-slate-400 text-sm">Your Number</span>
          </div>
          <div className="text-lg font-bold truncate">
            {commStats?.twilio_phone || 'Not assigned'}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <PhoneCall className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Total Calls</span>
          </div>
          <div className="text-3xl font-bold text-blue-400">{commStats?.total_calls || 0}</div>
          <div className="text-xs text-slate-500">{commStats?.calls_this_week || 0} this week</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Total SMS</span>
          </div>
          <div className="text-3xl font-bold text-green-400">{commStats?.total_sms || 0}</div>
          <div className="text-xs text-slate-500">{commStats?.sms_this_week || 0} this week</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-purple-400" />
            <span className="text-slate-400 text-sm">Conversations</span>
          </div>
          <div className="text-3xl font-bold text-purple-400">{conversations.length}</div>
        </motion.div>
      </div>

      {/* SMS Conversations */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-400" />
            SMS Conversations
          </h2>
        </div>

        {conversations.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No messages yet</h3>
            <p className="text-slate-500 text-sm">
              When customers text your AI number, conversations will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {conversations.map((conv) => (
              <div key={conv.phone_number} className="hover:bg-slate-800/50 transition-colors">
                <button
                  className="w-full px-6 py-4 text-left"
                  onClick={() => setExpandedConversation(
                    expandedConversation === conv.phone_number ? null : conv.phone_number
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium">{conv.phone_number}</p>
                        <p className="text-sm text-slate-400">
                          {conv.messages.length} messages
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">
                        {formatDate(conv.last_message_at)}
                      </span>
                      {expandedConversation === conv.phone_number ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </button>
                
                {expandedConversation === conv.phone_number && (
                  <div className="px-6 pb-4 space-y-3 bg-slate-800/30">
                    {conv.messages.slice(0, 10).map((msg, msgIdx) => (
                      <div
                        key={msgIdx}
                        className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] rounded-lg p-3 ${
                          msg.direction === 'outgoing'
                            ? 'bg-sky-500/20 text-sky-100'
                            : 'bg-slate-700 text-slate-200'
                        }`}>
                          <p className="text-sm">{msg.body}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {conv.messages.length > 10 && (
                      <p className="text-center text-sm text-slate-400">
                        +{conv.messages.length - 10} more messages
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call History */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-blue-400" />
            Recent Calls
          </h2>
        </div>

        {calls.length === 0 ? (
          <div className="text-center py-12">
            <PhoneCall className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No calls yet</h3>
            <p className="text-slate-500 text-sm">
              When customers call your AI number, call logs will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800 max-h-[400px] overflow-y-auto">
            {calls.slice(0, 20).map((call, idx) => (
              <div key={idx} className="px-6 py-4 hover:bg-slate-800/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <PhoneIncoming className="w-5 h-5 text-blue-400" />
                    <span className="font-medium">{call.from_number}</span>
                  </div>
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    call.status === 'completed' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-slate-500/20 text-slate-400'
                  }`}>
                    {call.status || 'in_progress'}
                  </span>
                </div>
                {call.transcript && (
                  <p className="text-sm text-slate-300 mb-2 truncate">"{call.transcript}"</p>
                )}
                {call.ai_response && (
                  <p className="text-sm text-sky-400 mb-2 truncate">AI: {call.ai_response}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{formatDate(call.created_at)}</span>
                  {call.duration && <span>Duration: {Math.floor(call.duration / 60)}:{(call.duration % 60).toString().padStart(2, '0')}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phone Service Status */}
      {!commStats?.phone_active && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-400 mb-1">Phone Service Not Active</h3>
              <p className="text-slate-300 text-sm">
                You don't have an AI phone number assigned yet. Contact support to activate your phone service
                and start receiving calls and texts through your AI assistant.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
